





























import { computed, defineComponent, onBeforeUnmount, PropType, ref, watch } from '@vue/composition-api';
import { useContext } from '@berlin-front/common/composables/context';
import { ThemeContext } from '@berlin-front/common/services/ThemeContext';
import PatchedVTextField from '@berlin-front/common/components/elements/PatchedVTextField';
import { bemx } from '@berlin-front/common/utils';
import { TextAlignedType, TEXT_ALIGNED, useTextFieldAttribute } from './composable';

/**
 * ## テキストフィールド
 *
 * モジュール内の標準的なテキストフィールドを扱うコンポーネント。
 * テキスト入力、数値入力のテキストフィールドが対象。
 * メールアドレス入力、認証コード入力は別コンポーネントで管理する。
 * 見た目はVuetify標準のOutlinedをベースに、色とラベルを一部CSSを上書きしている。
 * 入力要素（`value`）はv-modelを使いv-text-fieldと値を連動させること。
 * heightは標準で56px、`dense`を指定すると低くなり40pxとなる。
 * widthは可変で親の幅に合わせる前提のため、このコンポーネントへの指定はなし。
 *
 * `aligned`でinputフォームのテキストの左・中央・右寄せを指定する。デフォルトで左寄せ。
 * `numerical`を指定すると数値入力フォームになる。
 * `clearable`を指定するとVuetify標準のクリアアイコンが表示される。
 *
 * バリデーションの指定は親コンポーネントで実施する。
 * エラーメッセージを親から受け取り、本コンポーネントは親側へeventの伝搬のみを行う。
 *
 * Vuetifyの`v-text-field`については以下を参照。
 *
 * - 仕様書：https://vuetifyjs.com/ja/api/v-text-field/
 * - コード：https://github.com/vuetifyjs/vuetify/tree/master/packages/vuetify/src/components/VTextField
 *
 */
export default defineComponent({
  name: 'AppInput',
  components: {
    PatchedVTextField,
  },

  props: {
    /** テキスト配置。
     * 左・中央・右寄せを指定する。デフォルトで左寄せ。
     */
    aligned: {
      type: String as PropType<TextAlignedType>,
      default: TEXT_ALIGNED.LEFT,
    },

    /** テキストクリア。
     * 指定するとVuetify標準のクリアアイコンが表示される。
     * */
    clearable: {
      type: Boolean,
      default: false,
    },

    /** 隙間を詰める。
     * 指定すると高さが標準（56px）よりも低く（40px）なる。
     * */
    dense: {
      type: Boolean,
      default: false,
    },

    /** 無効化。 */
    disabled: {
      type: Boolean,
      default: false,
    },

    hideDetails: {
      type: Boolean,
      default: false,
    },

    id: {
      type: String,
      required: false,
      default: () => undefined,
    },

    /** エラーメッセージ。
     * バリデーションエラー時に表示するメッセージ。
     * バリデーションオブジェクトの状態によって変化する。
     * */
    errorMessages: {
      type: Array as PropType<string[] | unknown[]>,
      default: null,
    },

    /** ヒント。
     * フォーカスで補足説明が表示される。
     * */
    hint: {
      type: String,
      default: '',
    },

    /** ラベル。
     * テキストフィールドの項目ラベルとして使用される。
     * 正式なフィールド名でなく、簡易的な名称をラベルに設定しても問題なし。
     * また、テキストフィールドによってはラベル名の記載がないものもある。
     * */
    label: {
      type: String,
      default: '',
    },

    /** メッセージ非表示。
     * 指定するとバリデーションメッセージを表示領域ごと消す。
     * */
    noMessage: {
      type: Boolean,
      default: false,
    },

    /** 数値化。 数値入力フォームで指定する。 */
    numerical: {
      type: Boolean,
      default: false,
    },

    /** 電話番号フォーム。 */
    phoneNumber: {
      type: Boolean,
      default: false,
    },

    /** プレースホルダー。 必要に応じて入力例を設定する。 */
    placeholder: {
      type: String,
      default: '',
    },

    /** 読取専用。 */
    readonly: {
      type: Boolean,
      default: false,
    },

    maxLength: {
      type: Number,
      required: false,
      default: () => undefined,
    },

    /** 入力値。 v-modelで指定した値が連動される。 */
    value: {
      type: [String, Number] as PropType<string | number>,
      default: null,
    },

    /**
     * フォームに設定する名前属性。
     *
     * ブラウザのautocompleteに必要なため設定するようになっている。
     */
    name: {
      type: String,
      required: false,
      default: '',
    },

    /**
     * オートコンプリート属性
     */
    autocomplete: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: [
    // 多分input要素の全イベントを伝搬出来るので、必要になり次第都度都度emitsに追加してね。
    /**
     * inputイベント
     */
    'input',
    /**
     * blurイベント
     * @type {Event}
     */
    'blur',
    /**
     * htmlの生のchangeイベント
     * @type { Event }
     */
    'change',
    /**
     * IME変換終了イベント
     */
    'compositionend',
  ],

  setup(props, { emit, listeners }) {
    // vuetifyのchangeイベントにバグがあって、適切に発火しないため、ここで修正する。
    // まずはコンポーネント引数のイベントハンドラから、changeイベントのハンドラだけ引き剥がす。
    const computedListeners = computed(() =>
      Object.fromEntries(Object.entries(listeners).filter(([entryName]) => entryName !== 'change')),
    );
    // 生のinput elementに直接changeイベントハンドラを仕掛けてemitする。
    const changeEventHandler = (ev: Event) => emit('change', ev);
    const componentRef = ref();
    const inputElementRef = computed(() => {
      const el = componentRef.value?.$refs?.input;
      return el instanceof HTMLInputElement ? el : undefined;
    });
    watch(inputElementRef, (newElement, oldElement) => {
      newElement?.addEventListener('change', changeEventHandler);
      oldElement?.removeEventListener('change', changeEventHandler);
    });
    onBeforeUnmount(() => {
      inputElementRef.value?.removeEventListener('change', changeEventHandler);
    });

    // 条件に応じたテキストフィールド属性を取得
    const textFieldAttribute = useTextFieldAttribute(props.phoneNumber, props.numerical);

    const { themeParameter } = useContext(ThemeContext);
    const primaryColor = computed(() => themeParameter.value.primaryColor);

    const className = computed(() => bemx('AppInput', { align: props.aligned, noMessage: props.noMessage }));

    const ac = computed(() => props.autocomplete || textFieldAttribute.value.autocomplete);

    // returnするもののうち、親から参照出来るように公開するものをここに書くが、Vue2ではexposeは使えず全公開になる。
    // @see https://github.com/vuejs/composition-api/issues/849
    // context.expose({ inputElement: inputElementRef });

    return {
      inputElement: inputElementRef,
      componentRef,
      ac,
      className,
      primaryColor,
      textFieldAttribute,
      listeners: computedListeners,
    };
  },
});
